import { Fragment, type FC, type ReactNode } from 'react';
import styled from 'styled-components';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { ReactComponent as ManufacturerIcon } from '../icons/manufacturer.svg';
import matLogo from '@/assets/images/matlogo.svg';
import matLogoWhite from '@/assets/images/matlogo_white_blue.svg';
import { useInjection } from 'inversify-react';
import type { AppController } from '@/engine/app/App.controller';
import { TYPES } from '@/engine/ioc';
import { Typography, useTheme } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { ReactComponent as UDIIcon } from '../icons/UDI.svg';
import { ReactComponent as REFIcon } from '../icons/REF.svg';
import { ReactComponent as DateOfmanufacture } from '../icons/date_of_manufacture.svg';
import { ReactComponent as LOT } from '../icons/LOT.svg';
import { ReactComponent as RX } from '../icons/rxonly.svg';
import { ReactComponent as Caution } from '../icons/caution.svg';
import { ReactComponent as MedicalDevice } from '../icons/medical_device.svg';
import { ReactComponent as Consult } from '../icons/consult_use.svg';
import { ReactComponent as CE } from '../icons/ce-0123.svg';
import { useSurgerySpecificUiConfigValue } from '@/engine/app/modules/ui';
import type { IAboutInfo } from '@/engine/app/modules/ui/UIConfigSchema';
import { ModalState } from '@/components/header/types';
import { ModalLayout } from '../ModalLayout';
import ChRepIcon from '../icons/ch_rep.png';

export interface ModalProps {
  modalStateChangeHandler: (modal: ModalState) => void;
  isVisible: boolean;
}

export interface DialogTitleProps {
  id: string;
  children?: ReactNode;
  onClose: () => void;
}

export const HelpAndAboutModal: FC<ModalProps> = (props) => {
  const appController = useInjection<AppController>(TYPES.AppController);
  const envInfo = appController.getEnvInfo();
  const aboutInfo = useSurgerySpecificUiConfigValue<IAboutInfo>('aboutInfo');
  const theme = useTheme();

  return (
    <ModalLayout title="Help and About" {...props}>
      <DialogContent dividers>
        <ModalContentBody>
          <ModalContentBodyVersions>
            <ContentNameWrapper>
              <h2>
                {aboutInfo.productName} {aboutInfo.version} {aboutInfo.versionSuffix}
              </h2>
              <p>
                {aboutInfo.shortName} application version {envInfo.productInfoBuildUIVersion}
              </p>
              <p>
                {aboutInfo.shortName} API version {aboutInfo.version}
              </p>
            </ContentNameWrapper>
            <IconWrapper>
              <img src={theme.mode === 'dark' ? matLogoWhite : matLogo} className="mat-logo" alt="Materialise Logo" />
            </IconWrapper>
          </ModalContentBodyVersions>
          <ModalContentBodyLinks>
            <PictureAsPdfIcon />
            <a href={aboutInfo.manualUrl} target="_blank" rel="noreferrer">
              Software User Manual
            </a>
          </ModalContentBodyLinks>
          <p className="current-year">&copy; {aboutInfo.releaseYear}, Materialise NV, All rights reserved.</p>
          <ModalContentBodyPoweredBy></ModalContentBodyPoweredBy>
        </ModalContentBody>
      </DialogContent>
      <DialogContent dividers>
        {aboutInfo.notForClinicalMsg !== '' && (
          <ContentNameWrapper>
            <p className="not-for-clinical">{aboutInfo.notForClinicalMsg}</p>
          </ContentNameWrapper>
        )}
        <h3>Regulatory information</h3>
        <ModalContentRegulatoryInfo>
          <IconWrapper className="grow ref">
            <REFIcon />
            <p>{aboutInfo.productName}</p>
          </IconWrapper>
          <IconWrapper className="grow udi">
            <UDIIcon />
            <p>{aboutInfo.udiLabel}</p>
          </IconWrapper>
          <IconWrapper>
            <CE />
          </IconWrapper>
          <IconWrapper>
            <ManufacturerIcon />
            <p>
              Materialise NV
              <br />
              Technologielaan 15
              <br />
              3001 Leuven, Belgium
              <br />
              <a href="https://materialise.com" target="_blank" rel="noopener noreferrer">
                materialise.com
              </a>
            </p>
          </IconWrapper>
          <IconWrapper>
            <DateOfmanufacture />
            <p>{aboutInfo.releaseDate}</p>
          </IconWrapper>
          <IconWrapper>
            <LOT />
            <p>{aboutInfo.version}</p>
          </IconWrapper>
          {aboutInfo.rxOnlyLogo === true && (
            <IconWrapper>
              <RX />
            </IconWrapper>
          )}
          <IconWrapper>
            <Caution />
          </IconWrapper>
          <IconWrapper>
            <MedicalDevice />
          </IconWrapper>
          <IconWrapper>
            <Consult />
            <p>
              <a
                href="https://www.materialise.com/electronic-instructions-for-use"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.materialise.com/electronic-instructions-for-use
              </a>
            </p>
          </IconWrapper>
          {aboutInfo.ch_rep && aboutInfo.ch_rep.length > 0 && (
            <IconWrapper>
              <img src={ChRepIcon} />
              <p>
                <strong>CH authorised representative:</strong>
                <br />
                {aboutInfo.ch_rep.map((line, i) => (
                  <Fragment key={i}>
                    {line}
                    <br />
                  </Fragment>
                ))}
              </p>
            </IconWrapper>
          )}
        </ModalContentRegulatoryInfo>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.8em' }}>{aboutInfo.medicalLabel}</Typography>
        <Button
          autoFocus
          onClick={(): void => {
            props.modalStateChangeHandler(ModalState.None);
          }}
        >
          Ok
        </Button>
      </DialogActions>
    </ModalLayout>
  );
};

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 5px 5px;

  svg {
    width: 3.5em;
    height: 3.5em;
    margin-right: 5px;
  }
  img {
    width: 3.5em;
    margin-right: 5px;

    &.mat-logo {
      width: 10em;
    }
  }
  p {
    font-size: 0.7em;
  }
`;

const ModalContentRegulatoryInfo = styled.div`
  display: flex;
  column-gap: 0.5em;
  flex-wrap: wrap;

  & .grow {
    flex-grow: 1;
  }
  & .udi {
    font-family: Consolas, monaco, monospace;
    width: 100%;
  }
  & .ref {
    font-family: Consolas, monaco, monospace;
    width: 100%;
  }
`;

const ContentNameWrapper = styled.div`
  padding: 0 0 0.3em 0px;

  h2 {
    font-size: 1.6em;
    margin-top: 0.8em;
    font-weight: 400;
  }

  h3 {
    font-size: 1.1em;
    margin-top: 0.8em;
    font-weight: 600;
  }

  p {
    padding: 0.1em 0px 0.5em 0em;
    font-size: 0.6em;
    margin-top: 0.1em;
    overflow: visible;
  }
`;

const ModalContentBody = styled.div`
  p.current-year {
    text-align: right;
    font-size: 0.8em;
  }
  p.not-for-clinical {
    font-size: 0.8em;
  }
`;

const ModalContentBodyVersions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1em;
`;

const ModalContentBodyLinks = styled.div`
  display: flex;
  column-gap: 0.5em;
`;

const ModalContentBodyPoweredBy = styled.div``;
